import { useState, useEffect } from 'react';

// Returns true if viewport is mobile width
export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);

  return isMobile;
}

// Inject global responsive styles once
let injected = false;
export function injectResponsiveStyles() {
  if (injected || typeof document === 'undefined') return;
  injected = true;

  const style = document.createElement('style');
  style.textContent = `
    /* ── AUTH PAGE ── */
    .auth-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      min-height: 100vh;
    }
    .auth-brand-panel {
      display: flex;
    }
    @media (max-width: 768px) {
      .auth-grid {
        grid-template-columns: 1fr;
      }
      .auth-brand-panel {
        display: none;
      }
      .auth-form-panel {
        padding: 40px 24px !important;
      }
    }

    /* ── FAMILY DASHBOARD ──
       Desktop: hero spans full width on top.
       Below that, "Topics to Explore" on the left spans two rows,
       "Suggest a question" top-right and "Story progress" below it.
       Then full-width "Suggested Questions by Priority" feed (2-col grid).
       Then full-width "Chapters so far" (2-col grid).
         hero      hero
         topics    qsuggest
         topics    qprogress
         qfeed     qfeed
         chapters  chapters
       Mobile (≤900px): single column in this order
         hero → qsuggest → topics → qfeed → chapters → qprogress */
    .family-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-areas:
        "hero      hero"
        "topics    qright"
        "qfeed     qfeed"
        "chapters  chapters";
      gap: 24px 28px;
      max-width: 1100px;
      margin: 0 auto;
      padding: 40px 24px 80px;
      align-items: start;
    }
    .family-area-hero      { grid-area: hero; }
    .family-area-topics    { grid-area: topics; }
    .family-area-qright    {
      grid-area: qright;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .family-area-qfeed     { grid-area: qfeed; }
    .family-area-chapters  { grid-area: chapters; }

    @media (max-width: 900px) {
      .family-grid {
        grid-template-columns: 1fr;
        grid-template-areas:
          "hero"
          "qright"
          "topics"
          "qfeed"
          "chapters";
        padding: 20px 16px 60px;
        gap: 20px;
        overflow-x: hidden;
        max-width: 100vw;
      }
      .family-grid > div {
        min-width: 0;
        overflow-x: hidden;
      }
    }

    /* ── FAMILY HERO ── */
    .family-hero {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 24px;
      flex-wrap: wrap;
    }
    .family-hero-stats {
      display: flex;
      gap: 28px;
      flex-shrink: 0;
    }
    @media (max-width: 600px) {
      .family-hero {
        flex-direction: column;
        align-items: flex-start;
      }
      .family-hero-stats {
        gap: 16px;
      }
    }

    /* ── CHAPTER CARDS GRID ── */
    .chapters-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-bottom: 40px;
    }
    @media (max-width: 600px) {
      .chapters-grid {
        grid-template-columns: 1fr;
      }
    }

    /* Questions grid — 2 columns matching the chapters grid */
    .family-qfeed-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }
    @media (max-width: 600px) {
      .family-qfeed-grid {
        grid-template-columns: 1fr;
      }
    }

    /* ── BOOK PAGE ── */
    .book-grid {
      display: flex;
      flex-direction: column;
      gap: 40px;
      max-width: 1400px;
      margin: 0 auto;
      padding: 40px 24px 80px;
    }
    .book-order-sidebar {
      max-width: 480px;
      margin: 0 auto;
      width: 100%;
    }
    @media (max-width: 900px) {
      .book-grid {
        padding: 20px 16px 60px;
        gap: 24px;
      }
    }
    @media (max-width: 700px) {
      .book-grid {
        padding: 6px 8px 0 8px;
        gap: 8px;
      }
    }

    /* ── BOOK HERO ── */
    .book-hero {
      display: flex;
      align-items: flex-start;
      gap: 36px;
      margin-bottom: 48px;
      flex-wrap: wrap;
    }
    @media (max-width: 600px) {
      .book-hero {
        flex-direction: column;
        align-items: center;
        text-align: center;
        margin-bottom: 28px;
        gap: 20px;
      }
    }

    /* ── EDITOR PAGE ── */
    .editor-shell {
      height: 100vh;
      display: grid;
      grid-template-rows: 60px 52px 1fr;
      grid-template-columns: 1fr 300px;
    }
    .editor-preview-panel {
      display: flex;
      flex-direction: column;
      grid-row: 2 / 4;
      grid-column: 2;
    }
    .editor-workspace {
      grid-row: 3;
      grid-column: 1;
    }
    @media (max-width: 900px) {
      .editor-shell {
        grid-template-columns: 1fr;
        grid-template-rows: 60px 52px 1fr;
        height: auto;
        min-height: 100vh;
      }
      .editor-preview-panel {
        display: none;
      }
      .editor-workspace {
        grid-row: 3;
        grid-column: 1;
        padding: 20px 16px !important;
      }
    }

    /* ── EDITOR PREFACE WORKSPACE ── */
    .preface-workspace {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
    }
    @media (max-width: 768px) {
      .preface-workspace {
        grid-template-columns: 1fr;
      }
    }

    /* ── EDITOR COVER TEMPLATES ── */
    .cover-templates-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 14px;
      margin-bottom: 28px;
    }
    @media (max-width: 500px) {
      .cover-templates-grid {
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
      }
    }

    /* ── STORY STUDIO ── */
    .studio-shell {
      height: 100vh;
      height: 100dvh;
      display: grid;
      grid-template-rows: 60px 52px 1fr;
      grid-template-columns: 1fr;
      overflow: hidden;
    }
    .studio-workspace {
      grid-row: 3;
      grid-column: 1;
      overflow-y: auto;
      min-height: 0;
    }
    @media (max-width: 900px) {
      .studio-shell {
        height: 100vh;
        height: 100dvh;
      }
      .studio-workspace {
        padding: 20px 16px !important;
      }
      .studio-workspace[data-tab="chat"] {
        padding: 0 !important;
      }
    }
    @media (max-width: 600px) {
      .studio-shell {
        grid-template-rows: 60px 44px 1fr;
      }
    }

    /* ── FOOTER MOBILE COMPACT ── */
    @media (max-width: 600px) {
      .site-footer {
        padding: 20px 16px 16px !important;
      }
      .site-footer .footer-top {
        gap: 16px !important;
        margin-bottom: 16px !important;
      }
      .site-footer .footer-brand {
        margin-bottom: 4px !important;
      }
      .site-footer .footer-brand p:first-child {
        font-size: 17px !important;
        margin-bottom: 4px !important;
      }
      .site-footer .footer-brand p:last-child {
        font-size: 12px !important;
      }
      .site-footer .footer-nav {
        gap: 20px !important;
      }
      .site-footer .footer-nav-group p {
        margin-bottom: 6px !important;
      }
      .site-footer .footer-nav-links {
        gap: 6px !important;
      }
      .site-footer .footer-social-label {
        margin: 12px 0 8px !important;
      }
      .site-footer .footer-bottom {
        padding-top: 12px !important;
        gap: 6px !important;
      }
    }

    /* ── HEADER LOGO STACKING ── */
    @media (max-width: 600px) {
      .site-logo .logo-full { display: none !important; }
      .site-logo .logo-stacked { display: flex !important; align-items: flex-start; }
      .site-logo { font-size: 17px !important; line-height: 1.15 !important; }
    }

    /* ── GENERAL MOBILE POLISH ── */
    @media (max-width: 768px) {
      /* Larger tap targets for elderly users */
      button { min-height: 44px; }

      /* Prevent horizontal overflow */
      html, body { overflow-x: hidden; max-width: 100vw; }
      #root { overflow-x: hidden; max-width: 100vw; }

      /* Better padding on cards */
      .mobile-pad { padding: 16px !important; }
    }
  `;
  document.head.appendChild(style);
}
